import { useEffect, useState } from "react";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { Link, useSearchParams } from "react-router-dom";
import FaIcon from "../components/FaIcon";
import { CARD_BACKGROUNDS } from "../constants/loadout";
import { 
  Users, Crown, Shield, ShieldCheck, 
  Star, User, Award, Flame, Search, ArrowUpDown,
  X
} from "lucide-react";

const iconMap = {
  users: "fa-solid fa-users",
  crown: "fa-solid fa-crown",
  shield: "fa-solid fa-shield",
  "shield-check": "fa-solid fa-shield-halved",
  star: "fa-solid fa-star",
  user: "fa-solid fa-user",
  award: "fa-solid fa-trophy",
  flame: "fa-solid fa-fire",
};

const GroupHeaderIcon = ({ icon, color, className }) => {
  if (icon && (icon.startsWith("http") || icon.includes("/"))) {
    return <img src={icon} alt="Icon" className={`${className} object-contain`} style={{ width: 18, height: 18 }} />;
  }
  const faName = iconMap[icon] || "fa-solid fa-users";
  return <FaIcon name={faName} className={className} style={{ color }} />;
};

function WeaponBadge({ name }) {
  if (!name) return null;
  const lower = name.toLowerCase();
  let color = "text-zinc-400 bg-zinc-900/80 border-zinc-800";
  let faIcon = "fa-solid fa-crosshairs";

  if (lower.includes("awp")) {
    color = "text-emerald-400 bg-emerald-950/50 border-emerald-500/30";
    faIcon = "fa-solid fa-crosshairs";
  } else if (lower.includes("ak") || lower.includes("m4")) {
    color = "text-amber-400 bg-amber-950/50 border-amber-500/30";
    faIcon = "fa-solid fa-bolt";
  } else if (lower.includes("deagle") || lower.includes("desert")) {
    color = "text-cyan-400 bg-cyan-950/50 border-cyan-500/30";
    faIcon = "fa-solid fa-gun";
  }

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono font-bold border ${color}`}>
      <FaIcon name={faIcon} className="text-[9px]" />
      <span className="truncate max-w-[90px]">{name}</span>
    </span>
  );
}

function MemberCard({ user }) {
  const publicUrl = process.env.PUBLIC_URL || "/forum";
  const accentColor = user.group_color || "#FF4F00";
  const cardBgOpt = CARD_BACKGROUNDS.find((b) => b.id === user.loadout?.card_background);
  
  const bannerImage = (user.cover_banner_url || user.banner_url)
    ? (user.cover_banner_url || user.banner_url)
    : (cardBgOpt && cardBgOpt.id !== "none" && cardBgOpt.style?.backgroundImage)
      ? cardBgOpt.style.backgroundImage
      : `${publicUrl}/banners/default_profile_banner.png`;

  const hasVip = (user.loadout?.banner_badge && user.loadout.banner_badge !== "none") || (user.vip_tier && user.vip_tier !== "none") || user.is_vip;
  
  // Format joined date
  const joinedDate = user.joined_at || user.created_at;
  const formattedDate = joinedDate 
    ? new Date(joinedDate).toISOString().split('T')[0]
    : "2026-01-01";

  const topicsCount = user.topic_count || user.topics_count || 0;
  const postsCount = user.message_count || user.messages_count || 0;
  const points = user.points || 0;
  const reactions = user.reaction_score || user.reputation || 0;

  return (
    <div className="group bg-[#090d16] hover:bg-[#0d1322] border border-[#161f30] hover:border-[var(--tf-primary)]/60 rounded-xl overflow-hidden transition-all duration-300 shadow-xl flex flex-col relative hover:-translate-y-1">
      
      {/* 1. Header Banner */}
      <div 
        className="h-24 w-full relative bg-[#121824] bg-cover bg-center overflow-hidden shrink-0"
        style={{
          backgroundImage: `url(${bannerImage}), linear-gradient(135deg, #161e2e 0%, #0a0d14 100%)`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#090d16] via-[#090d16]/40 to-transparent" />
        
        {/* Steam quick badge on top right */}
        {user.steam_id && (
          <a 
            href={`https://steamcommunity.com/profiles/${user.steam_id}`} 
            target="_blank" 
            rel="noreferrer"
            className="absolute top-2 right-2 px-2 py-0.5 rounded bg-black/60 hover:bg-black/90 border border-cyan-500/40 text-cyan-400 text-[10px] font-mono flex items-center gap-1 transition-colors z-10"
            title="Profil Steam"
            onClick={(e) => e.stopPropagation()}
          >
            <FaIcon name="fa-brands fa-steam" className="text-[10px]" />
            <span>Steam</span>
          </a>
        )}
      </div>

      {/* 2. Square Avatar & Header Section */}
      <div className="px-4 -mt-10 relative z-10 flex items-end justify-between gap-3">
        {/* Square Avatar (rounded-xl) with Glow */}
        <Link to={`/u/${user.username}`} className="relative shrink-0 block group-hover:scale-105 transition-transform">
          <div 
            className="p-1 rounded-xl bg-[#090d16] border-2 shadow-2xl overflow-hidden"
            style={{ borderColor: accentColor }}
          >
            <Avatar user={user} size={58} hover={false} />
          </div>
          <span 
            className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-[#090d16] shadow-[0_0_8px_#10b981]" 
            title="Online" 
          />
        </Link>

        {/* Primary Role Pill Badge */}
        <div className="mb-1 flex flex-col items-end gap-1">
          {user.role === "root" && (
            <span className="px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-500 text-black border border-amber-400 font-chakra shadow-sm rounded-full">
              ROOT
            </span>
          )}
          {user.role === "admin" && user.role !== "root" && (
            <span className="px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white border border-red-500 font-chakra shadow-sm rounded-full">
              ADMIN
            </span>
          )}
          {user.role === "mod" && (
            <span className="px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-blue-600 text-white border border-blue-500 font-chakra shadow-sm rounded-full">
              MODERATOR
            </span>
          )}
          {hasVip && (
            <span className="px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-400/20 text-amber-300 border border-amber-400/40 font-chakra shadow-sm rounded-full">
              FORUMS VIP
            </span>
          )}
          {!hasVip && user.group_name && user.role !== "root" && user.role !== "admin" && user.role !== "mod" && (
            <span 
              className="px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider font-chakra border rounded-full"
              style={{
                backgroundColor: `${user.group_color || '#71717a'}20`,
                borderColor: `${user.group_color || '#71717a'}40`,
                color: user.group_color || '#fff'
              }}
            >
              {user.group_name}
            </span>
          )}
        </div>
      </div>

      {/* 3. User Details Info */}
      <div className="p-4 pt-2.5 flex-1 flex flex-col justify-between space-y-3">
        
        {/* Username and Team / Secondary Group */}
        <div>
          <Link to={`/u/${user.username}`}>
            <Username 
              user={user} 
              showBadge={false} 
              asText 
              hover={false} 
              className="text-base font-black font-chakra truncate text-white group-hover:text-[var(--tf-primary)] transition-colors block" 
            />
          </Link>

          {/* Secondary Group / Team Badge with Trophy icon */}
          <div className="flex items-center gap-2 mt-1 text-xs font-mono text-zinc-400">
            {user.secondary_group ? (
              <span className="flex items-center gap-1 text-amber-300">
                <FaIcon name="fa-solid fa-trophy" className="text-xs text-amber-400" />
                <span>{user.secondary_group}</span>
              </span>
            ) : user.group_name ? (
              <span className="flex items-center gap-1.5" style={{ color: accentColor }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }} />
                <span>{user.group_name}</span>
              </span>
            ) : (
              <span className="text-zinc-500">Membru Comunitate</span>
            )}
          </div>
        </div>

        {/* 4. Joined Date & Activity Grid */}
        <div className="space-y-1.5 bg-[#05070d] p-2.5 rounded-lg border border-[#141b2a] text-[11px] font-mono">
          <div className="flex items-center justify-between text-zinc-400 pb-1 border-b border-[#141b2a]">
            <span className="flex items-center gap-1.5 text-zinc-500">
              <FaIcon name="fa-solid fa-calendar-days" className="text-[10px]" />
              <span>Joined:</span>
            </span>
            <span className="text-zinc-300 font-bold">{formattedDate}</span>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-0.5">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Topics:</span>
              <span className="font-bold text-white">{topicsCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Posts:</span>
              <span className="font-bold text-[var(--tf-primary)]">{postsCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Puncte:</span>
              <span className="font-bold text-amber-400">★ {points}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Reacții:</span>
              <span className="font-bold text-orange-400">⚡ {reactions}</span>
            </div>
          </div>
        </div>

        {/* 5. Gaming Badges (Weapon / Game) */}
        {(user.loadout?.favorite_weapon || user.favorite_weapon || user.favorite_game) && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {(user.loadout?.favorite_weapon || user.favorite_weapon) && (
              <WeaponBadge name={user.loadout?.favorite_weapon || user.favorite_weapon} />
            )}
            {user.favorite_game && (
              <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-[#05070d] border border-amber-500/30 text-amber-400 flex items-center gap-1">
                <FaIcon name="fa-solid fa-gamepad" className="text-[9px]" />
                <span className="uppercase">{user.favorite_game}</span>
              </span>
            )}
          </div>
        )}

        {/* 6. Action Footer Buttons */}
        <div className="pt-2 border-t border-[#141b2a] grid grid-cols-2 gap-2">
          <Link 
            to={`/u/${user.username}`}
            className="py-1.5 px-3 rounded-lg bg-[#05070d] hover:bg-[var(--tf-primary)] hover:text-black border border-[#161f30] hover:border-[var(--tf-primary)] text-zinc-300 font-chakra font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all text-center"
          >
            <FaIcon name="fa-solid fa-user" className="text-[10px]" />
            <span>Profil</span>
          </Link>
          <Link 
            to={`/messages?new=${user.username}`}
            className="py-1.5 px-3 rounded-lg bg-[#05070d] hover:bg-cyan-500 hover:text-black border border-[#161f30] hover:border-cyan-500 text-zinc-300 font-chakra font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all text-center"
          >
            <FaIcon name="fa-solid fa-envelope" className="text-[10px]" />
            <span>Mesaj</span>
          </Link>
        </div>

      </div>

    </div>
  );
}

export default function Members() {
  const [groupsData, setGroupsData] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("points");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const groupFilter = searchParams.get("group");
  const [groupName, setGroupName] = useState("");
  const [legendGroups, setLegendGroups] = useState([]);

  const load = () => {
    setLoading(true);
    api.get(`/users/by-groups?q=${encodeURIComponent(q)}&sort=${sort}`)
      .then((r) => {
        setGroupsData(r.data || []);
      })
      .catch((err) => {
        console.error("Failed to load members:", err);
        setGroupsData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => { 
    load(); 
  }, [sort]);

  // Load group list for filter pills
  useEffect(() => {
    api.get("/idg/groups/legend")
      .then(r => {
        setLegendGroups(r.data || []);
        if (groupFilter) {
          const found = (r.data || []).find(g => g.id === groupFilter || g.slug === groupFilter);
          setGroupName(found?.name || "");
        }
      })
      .catch(() => {});
  }, [groupFilter]);

  const filteredGroups = groupFilter
    ? groupsData.filter((g) => g.id === groupFilter || g.slug === groupFilter)
    : groupsData;

  const totalMembersCount = groupsData.reduce((acc, g) => acc + (g.users?.length || 0), 0);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12" data-testid="members-page">
      
      {/* ─── 1. HERO OPERATOR DIRECTORY HEADER ─── */}
      <div className="relative rounded-2xl overflow-hidden border border-[#161f30] bg-[#070a10] shadow-2xl p-6 sm:p-8">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_top_right,rgba(0,255,136,0.08),transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,79,0,0.06),transparent_70%)] pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] font-mono font-black uppercase tracking-widest flex items-center gap-1.5">
                <FaIcon name="fa-solid fa-users" className="text-xs" />
                <span>INDUNGI // OPERATOR DIRECTORY</span>
              </span>
              <span className="text-[10px] font-mono text-zinc-400">
                · EST. 2010
              </span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-chakra font-black uppercase text-white tracking-tight leading-none">
              DIRECTORUL OFICIAL <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--tf-primary)] via-amber-400 to-cyan-400">MEMBRI & OPERATORI</span>
            </h1>

            <p className="text-zinc-400 text-xs sm:text-sm font-sans leading-relaxed">
              Explorează comunitatea Indungi x PlayZek: conducere, staff tehnic, donatori VIP, lideri de clanuri și jucători activi de Counter-Strike.
            </p>
          </div>

          {/* Metric Stats Pills */}
          <div className="flex items-center gap-3 relative z-10 shrink-0">
            <div className="px-4 py-3 bg-[#05070d]/90 border border-[#1a2336] rounded-xl flex items-center gap-3 shadow-lg">
              <div className="w-10 h-10 rounded-lg bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/30 flex items-center justify-center text-[var(--tf-primary)]">
                <FaIcon name="fa-solid fa-users" className="text-lg" />
              </div>
              <div>
                <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Total Înregistrați</div>
                <div className="text-xl font-chakra font-black text-white">{totalMembersCount || 96} Operatori</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── 2. GROUP FILTER PILLS ─── */}
      {legendGroups.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <button
            onClick={() => setSearchParams({})}
            className={`px-4 py-2 rounded-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border shrink-0 ${
              !groupFilter 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#080c14] border-[#161f30] text-zinc-400 hover:text-white hover:border-white/20"
            }`}
          >
            Toți Membrii
          </button>
          {legendGroups.map((lg) => {
            const isSelected = groupFilter === lg.id || groupFilter === lg.slug;
            return (
              <button
                key={lg.id}
                onClick={() => setSearchParams({ group: lg.id })}
                className={`px-4 py-2 rounded-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border shrink-0 flex items-center gap-1.5 ${
                  isSelected 
                    ? "text-black border-transparent shadow-md" 
                    : "bg-[#080c14] border-[#161f30] text-zinc-400 hover:text-white hover:border-white/20"
                }`}
                style={isSelected ? { backgroundColor: lg.color || "var(--tf-primary)", borderColor: lg.color } : {}}
              >
                <span className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: lg.color || "#fff" }} />
                <span>{lg.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ─── 3. SEARCH & SORT BAR ─── */}
      <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 shadow-lg">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input 
            value={q} 
            onChange={(e) => setQ(e.target.value)} 
            onKeyDown={(e) => e.key === "Enter" && load()} 
            placeholder="Caută operator după nume sau rol..." 
            className="w-full bg-[#05070d] border border-[#141b2a] rounded-lg pl-10 pr-4 py-2.5 text-xs font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)] transition-colors" 
            data-testid="members-search" 
          />
          {q && (
            <button 
              onClick={() => { setQ(""); load(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-[#05070d] border border-[#141b2a] px-3 py-1.5 rounded-lg text-xs font-mono text-zinc-400">
            <ArrowUpDown className="w-3.5 h-3.5 text-zinc-400" />
            <select 
              value={sort} 
              onChange={(e) => setSort(e.target.value)} 
              className="bg-transparent text-white focus:outline-none cursor-pointer uppercase font-bold text-[11px]" 
              data-testid="members-sort"
            >
              <option value="points" className="bg-[#080c14]">Top Puncte</option>
              <option value="messages" className="bg-[#080c14]">Cele mai multe Mesaje</option>
              <option value="recent" className="bg-[#080c14]">Recent Înregistrați</option>
              <option value="active" className="bg-[#080c14]">Recent Activi</option>
            </select>
          </div>

          <button
            onClick={load}
            className="px-5 py-2.5 rounded-lg bg-[var(--tf-primary)] hover:bg-[#ff6b2b] text-black font-chakra font-black text-xs uppercase tracking-wider transition-all shadow-md"
          >
            Caută
          </button>
        </div>
      </div>

      {/* ─── 4. ACTIVE GROUP FILTER NOTICE ─── */}
      {groupFilter && (
        <div className="flex items-center justify-between px-4 py-3 bg-[#080c14] border border-[var(--tf-primary)]/40 rounded-xl text-xs font-mono shadow-md">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--tf-primary)] animate-pulse" />
            <span className="text-zinc-400">
              Grup filtrat: <strong className="text-white font-chakra text-sm uppercase">{groupName || groupFilter}</strong>
            </span>
          </div>
          <button
            onClick={() => setSearchParams({})}
            className="text-[11px] font-chakra font-bold text-zinc-400 hover:text-white border border-white/10 px-3 py-1 rounded-md hover:bg-white/5 transition-colors flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            <span>Resetează filtrul</span>
          </button>
        </div>
      )}

      {/* ─── 5. MEMBERS DETAILED CARDS GRID BY GROUPS ─── */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="bg-[#080c14] border border-[#161f30] p-4 h-64 rounded-xl" />
          ))}
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-12 text-center text-zinc-400 font-mono text-xs">
          Niciun membru găsit conform criteriilor de căutare.
        </div>
      ) : (
        <div className="space-y-10">
          {filteredGroups.map((group) => (
            <div key={group.id} className="space-y-4">
              {/* Group Title Header */}
              <div className="flex items-center justify-between border-b border-[#141b2a] pb-3">
                <div className="flex items-center gap-2.5">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center shadow-md"
                    style={{ backgroundColor: `${group.color || 'var(--tf-primary)'}20`, border: `1px solid ${group.color || 'var(--tf-primary)'}40` }}
                  >
                    <GroupHeaderIcon icon={group.label_icon} color={group.color} className="text-sm" />
                  </div>
                  <h2 
                    className="font-chakra text-lg sm:text-xl font-black uppercase tracking-wide" 
                    style={{ color: group.color || "var(--tf-primary)" }}
                  >
                    {group.name}
                  </h2>
                </div>
                <span className="text-zinc-400 font-mono text-xs bg-[#05070d] px-3 py-1 rounded-full border border-white/5 font-bold">
                  {group.users?.length || 0} membri
                </span>
              </div>

              {/* Members 4-Column Card Grid (Like image 1) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {group.users.map((u) => (
                  <MemberCard key={u.id} user={u} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
